/**
 * Translates Gateway error codes (services/gateway/src/errors.ts, shape
 * `{ code, message }`) into plain, human-readable copy. This is the ONLY
 * place in apps/web allowed to hardcode a friendly error string mapped to
 * a code — every other part of the app should call `friendlyError()`
 * instead of inlining its own guess at what an error means.
 *
 * Philosophy (FRONTEND_REFERENCE.md section 1 and Joanfer's own framing):
 * transparent without being confusing. The technical code is never hidden
 * (it is shown, small, for support purposes) but it is never the primary
 * message either.
 */

interface FriendlyError {
  message: string
  code?: string
}

const KNOWN_CODES: Record<string, string> = {
  INVALID_CREDENTIALS: "El usuario o la contraseña no son correctos.",
  USERNAME_TAKEN: "Ese nombre de usuario ya está en uso.",
  UNAUTHENTICATED: "Tu sesión no es válida. Iniciá sesión de nuevo.",
  ACCESS_TOKEN_EXPIRED: "Tu sesión expiró. Iniciá sesión de nuevo.",
  REFRESH_TOKEN_INVALID: "Tu sesión expiró. Iniciá sesión de nuevo.",
  FORBIDDEN: "No tenés permiso para hacer esto.",
  ERR_HOST_OFFLINE:
    "La máquina host está apagada o sin conexión. Los servidores no están disponibles en este momento.",
  ERR_CRAFTY_UNREACHABLE:
    "El panel de control local no responde. Los servidores pueden estar corriendo, pero no es posible gestionarlos ahora.",
  SERVER_NOT_FOUND: "No encontramos ese servidor.",
  FAMILY_NOT_FOUND: "No encontramos esa familia.",
  INVALID_RUNTIME_CONFIG_KEY: "Esa opción de configuración no existe.",
  INVALID_RUNTIME_CONFIG_VALUE: "Ese valor no es válido para esta opción.",
  ERR_SERVER_MANAGER_UNREACHABLE:
    "El servicio que administra los servidores no respondió a tiempo. Puede estar reiniciándose, probá de nuevo en un momento.",
}

/**
 * ERR_SERVER_QUERY_FAILED is not mapped to a fixed copy in KNOWN_CODES
 * because its message is not a fixed string to begin with -- it carries
 * whatever craftyErrorMessage() produced on the server-manager side
 * (services/server-manager/src/nats/queryHandlers.ts), which already
 * includes the real reason Crafty rejected the request (e.g. the
 * INVALID_JSON_SCHEMA text for a bad server name). Treating this code as
 * unknown and discarding err.message threw away the one piece of
 * information the backend went out of its way to preserve across the
 * Gateway -> NATS -> Crafty round trip (see TODO.md, 'Pendiente ahora
 * mismo'). Passed through as-is instead of re-mapped to a canned string:
 * Crafty's wording varies per validation rule and guessing a friendlier
 * paraphrase risks silently going stale against Crafty's own copy.
 */
const PASSTHROUGH_CODES = new Set(["ERR_SERVER_QUERY_FAILED"])

const NETWORK_ERROR: FriendlyError = {
  message: "No pudimos conectar con el servidor. Revisá tu conexión e intentá de nuevo.",
}

const UNKNOWN_ERROR: FriendlyError = {
  message: "Hubo un problema inesperado. Intentá de nuevo en un momento.",
}

/**
 * `err` is whatever httpClient throws: an ApiError (parsed Gateway body)
 * or a raw network/fetch failure. Never returns a raw HTTP status or
 * stack trace to the caller — that's the whole point.
 */
export function friendlyError(err: unknown): FriendlyError {
  if (err instanceof ApiError) {
    if (PASSTHROUGH_CODES.has(err.code)) {
      return { message: err.message || UNKNOWN_ERROR.message, code: err.code }
    }
    const known = KNOWN_CODES[err.code]
    return known ? { message: known, code: err.code } : { message: UNKNOWN_ERROR.message, code: err.code }
  }
  if (err instanceof TypeError) {
    // fetch() throws a bare TypeError on network failure (DNS, offline, CORS).
    return NETWORK_ERROR
  }
  return UNKNOWN_ERROR
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly httpStatus: number,
  ) {
    super(message)
    this.name = "ApiError"
  }
}
